/**
 * MUI Components whose styles are override as per theme
 * @param {JsonObject} theme Plain Json Object
 */
export function componentStyleOverrides(theme) {
    const isDark = theme?.customization?.mode === 'dark';

    return {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                disableRipple: true
            },
            styleOverrides: {
                root: {
                    fontWeight: 590,
                    textTransform: 'none',
                    borderRadius: '10px',
                    padding: '8px 20px',
                    letterSpacing: '-0.01em',
                    transition: 'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s ease',
                    '&:hover': {
                        transform: 'translateY(-1px)'
                    },
                    '&:active': {
                        transform: 'translateY(0) scale(0.98)'
                    },
                    '&.Mui-disabled': {
                        transform: 'none'
                    }
                },
                contained: {
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                    '&:hover': {
                        boxShadow: `0 8px 20px ${theme.customization.primaryColor}3d`
                    }
                },
                containedSecondary: {
                    '&:hover': {
                        boxShadow: `0 8px 20px ${theme.customization.secondaryColor}3d`
                    }
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                        backgroundColor: `${theme.customization.primaryColor}0f`
                    }
                },
                outlinedSecondary: {
                    '&:hover': {
                        backgroundColor: `${theme.customization.secondaryColor}0f`
                    }
                },
                text: {
                    '&:hover': {
                        backgroundColor: `${theme.customization.primaryColor}0f`
                    }
                },
                sizeLarge: {
                    borderRadius: '12px',
                    padding: '11px 26px',
                    fontSize: '1rem'
                },
                sizeSmall: {
                    borderRadius: '8px',
                    padding: '5px 14px',
                    fontSize: '0.8125rem'
                }
            }
        },
        MuiIconButton: {
            defaultProps: {
                disableRipple: true
            },
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    transition: 'background-color 0.15s ease, transform 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'scale(1.06)'
                    },
                    '&:active': {
                        transform: 'scale(0.96)'
                    }
                }
            }
        },
        MuiFab: {
            styleOverrides: {
                root: {
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.12)',
                    transition: 'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.15s ease',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.18)'
                    }
                }
            }
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none'
                },
                rounded: {
                    borderRadius: theme.customization.borderRadius + 'px'
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '18px',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}`,
                    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 4px 20px rgba(15,23,42,0.06)',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
                    '&:hover': {
                        boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.4)' : '0 12px 32px rgba(15,23,42,0.12)'
                    }
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    color: theme.colors.textDark,
                    padding: '24px'
                },
                title: {
                    fontSize: '1.125rem'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '24px'
                }
            }
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '24px'
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    color: theme.darkTextPrimary,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    '&.Mui-selected': {
                        color: theme.menuSelected,
                        backgroundColor: theme.menuSelectedBack,
                        '&:hover': {
                            backgroundColor: theme.menuSelectedBack
                        },
                        '& .MuiListItemIcon-root': {
                            color: theme.menuSelected
                        }
                    },
                    '&:hover': {
                        backgroundColor: theme.menuSelectedBack,
                        color: theme.menuSelected,
                        '& .MuiListItemIcon-root': {
                            color: theme.menuSelected
                        }
                    }
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: theme.darkTextPrimary,
                    minWidth: '36px'
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: theme.textDark
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    color: isDark ? '#fff' : theme.textDark,
                    '&::placeholder': {
                        color: isDark ? '#aaa' : theme.darkTextSecondary
                    }
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: isDark ? '#aaa' : theme.darkTextSecondary
                },
                shrink: {
                    color: isDark ? '#fff' : theme.darkTextPrimary
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    background: isDark ? '#1e1e2f' : theme.colors.grey50,

                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? 'rgba(255,255,255,0.2)' : theme.colors.grey400
                    },

                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.colors.primaryLight
                    }
                },

                input: {
                    color: isDark ? '#fff' : theme.textDark
                }
            }
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: theme.colors.grey300
                    }
                },
                mark: {
                    backgroundColor: theme.paper,
                    width: '4px'
                },
                valueLabel: {
                    color: theme.colors.primaryLight
                }
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: theme.divider,
                    opacity: 1
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    color: theme.colors.primaryDark,
                    background: theme.colors.primary200
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: '8px',
                    '&.MuiChip-colorSuccess, &.MuiChip-colorError, &.MuiChip-colorWarning, &.MuiChip-colorPrimary, &.MuiChip-colorSecondary, &.MuiChip-colorInfo': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                    },
                    '&.MuiChip-deletable .MuiChip-deleteIcon': {
                        color: 'inherit'
                    }
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background-color 0.15s ease'
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottomColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    color: theme.paper,
                    background: theme.colors.grey700
                }
            }
        }
    };
}
